import { reactive } from "vue";
import { stepValidationRules } from "../validationRules/stepValidationRules";

/**
 * Composable for step validation in Onboarding Wizard
 * @param {number} stepNumber - Current step number (1, 2, 3)
 * @returns {object} - { errors, validate, isValid, getError }
 */
export function useStepValidation(stepNumber) {
  const errors = reactive({});

  /**
   * Validate step data
   * @param {object} formData - Form data to validate
   * @returns {boolean} - true if validation passed
   */
  const validate = (formData) => {
    // Clear previous errors
    Object.keys(errors).forEach((key) => delete errors[key]);

    const rules = stepValidationRules[stepNumber];
    if (!rules) {
      return true;
    }

    let isValid = true;

    // Run all validation rules for this step
    for (const rule of rules) {
      // Check if rule condition is met (if exists)
      if (rule.condition && !rule.condition(formData)) {
        continue;
      }

      // Run validator
      const result = rule.validator(formData);

      if (!result.isValid) {
        // Merge errors into reactive object
        Object.assign(errors, result.errors);
        isValid = false;
      }
    }

    return isValid;
  };

  /**
   * Get error message for specific field
   * @param {string} fieldName - Field name
   * @returns {string|null} - Error message or null
   */
  const getError = (fieldName) => {
    return errors[fieldName] || null;
  };

  /**
   * Check if specific field has error
   * @param {string} fieldName - Field name
   * @returns {boolean} - true if field has error
   */
  const hasError = (fieldName) => {
    return !!errors[fieldName];
  };

  /**
   * Clear all errors
   */
  const clearErrors = () => {
    Object.keys(errors).forEach((key) => delete errors[key]);
  };

  /**
   * Set custom error for a field
   * @param {string} fieldName - Field name
   * @param {string} message - Error message
   */
  const setError = (fieldName, message) => {
    errors[fieldName] = message;
  };

  return {
    errors,
    validate,
    getError,
    hasError,
    clearErrors,
    setError,
  };
}
